import { Box, Button, Checkbox, RadioButton } from "@minervaproject/fds";
import cuid from "cuid";
import { sortBy } from "lodash";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { PollConfirmationTextAndEditButton } from "./poll_response_form_parts";
import { PollVisibility } from "./poll_visibility";

import { FlightAPIContext } from "contexts/flight_api_context";
import { useAppDispatch, useArraySelector } from "hooks/redux_hooks";
import { useIsMobile } from "hooks/use_is_mobile";
import { createOrUpdatePollResponse } from "shared/reducers/classroom";
import { PollChoiceType, PollSessionStatusType, PollSessionType, PollType } from "shared/types";
import { classroomSelectors } from "store";
import { deleteInProgressPollResponse, saveInProgressPollResponse } from "utils/poll";
import { numberToLetter } from "utils/string";

const CHOICE_COLORS = [
  "redViolet",
  "orange",
  "blueGreenShade20",
  "violet",
  "blackTint40",
  "blueShade40",
  "blueTint20",
  "blackTint90",
  "redVioletShade40",
  "yellow",
  "yellowGreen",
  "greenShade40",
];

type NonLongFormPollResponseFormProps = {
  poll: PollType;
  pollSession: PollSessionType;
  userId: string;
  readOnly?: boolean;
};

// This component handles accepting a poll response from a student. After the poll is completed,
// it will continue to display the poll options and the student's response (without edit capabilities).
function NonLongFormPollResponseForm({
  poll,
  pollSession,
  userId,
  readOnly = false,
}: NonLongFormPollResponseFormProps) {
  const dispatch = useAppDispatch();
  const flightAPI = useContext(FlightAPIContext);
  const pollResponses = useArraySelector(
    classroomSelectors.selectPollResponsesByPollSessionIdAndUserId(pollSession.id, userId),
  );
  const pollResponse = pollResponses.length ? pollResponses[0] : null;
  const initialCuid = useMemo(() => cuid(), []);
  const isDraft = pollResponse ? pollResponse.is_draft : true;
  const choices = sortBy(poll.poll_choices, "index");
  const inProgressPollResponse = useMemo(() => {
    const inProgressPollResponses = JSON.parse(
      localStorage.getItem("poll-responses-in-progress") || "{}",
    );
    return inProgressPollResponses[pollSession.id];
  }, [pollSession.id]);
  const [selectedChoices, _setSelectedChoices] = useState<number[]>(
    !isDraft ? (pollResponse?.response as number[]) : inProgressPollResponse || [],
  );
  const setSelectedChoices = useCallback(
    (newChoices: number[]) => {
      _setSelectedChoices(newChoices);
      saveInProgressPollResponse(pollSession.id, newChoices);
    },
    [pollSession.id],
  );

  const isMobile = useIsMobile();
  const isPollCompleted = pollSession.status === PollSessionStatusType.Completed;
  const [isFinalSaveComplete, setIsFinalSaveComplete] = useState(false);

  const handleSubmit = useCallback(() => {
    if (selectedChoices.length === 0) {
      window.flightLogger.log(
        "[NonLongFormPollResponseForm] Poll response not saved: no choices selected",
      );
      return;
    }
    window.flightLogger.log(
      `[NonLongFormPollResponseForm] Saving response response=${selectedChoices}`,
    );
    dispatch(
      createOrUpdatePollResponse({
        id: pollResponse?.id || initialCuid,
        pollSessionId: pollSession.id,
        userId,
        response: selectedChoices,
        isDraft: false,
        submittedAt: flightAPI.getServerMilliseconds(),
        hideFromStudents:
          !poll.student_visible || pollSession.status === PollSessionStatusType.InProgress
            ? ["response"]
            : [],
      }),
    );
  }, [
    dispatch,
    poll.student_visible,
    pollResponse?.id,
    pollSession.id,
    pollSession.status,
    selectedChoices,
    flightAPI,
    userId,
    initialCuid,
  ]);

  useEffect(() => {
    // Final save: auto-submit draft responses when the poll is completed.
    // Only run in the non-readOnly instance to avoid duplicate submissions,
    // since a readOnly instance is also mounted when the poll completes.
    if (readOnly) return;
    if (isPollCompleted && isDraft && isFinalSaveComplete) {
      window.flightLogger.log(
        "[NonLongFormPollResponseForm] Skipping final save: already completed",
      );
      return;
    }
    if (isPollCompleted && isDraft && !isFinalSaveComplete) {
      if (selectedChoices.length === 0 && pollResponse?.id) {
        window.flightLogger.log(
          `[NonLongFormPollResponseForm] Setting selected choices from previous poll response (response=${pollResponse.response})`,
        );
        setSelectedChoices(pollResponse.response as number[]);
      } else {
        window.flightLogger.log("[NonLongFormPollResponseForm] Final save");
        handleSubmit();
        setIsFinalSaveComplete(true);
      }
    }
  }, [
    readOnly,
    isFinalSaveComplete,
    handleSubmit,
    isPollCompleted,
    isDraft,
    selectedChoices.length,
    pollResponse?.id,
    pollResponse?.response,
    setSelectedChoices,
  ]);

  useEffect(() => {
    const _saveInProgressPollResponse = () => {
      if (pollSession.status === PollSessionStatusType.InProgress) {
        saveInProgressPollResponse(pollSession.id, selectedChoices);
      }
    };
    window.addEventListener("pagehide", _saveInProgressPollResponse);

    return () => {
      if (pollResponse && pollSession.status === PollSessionStatusType.Completed) {
        deleteInProgressPollResponse(pollSession.id);
      }
      window.removeEventListener("pagehide", _saveInProgressPollResponse);
    };
  }, [pollResponse, pollSession.id, pollSession.status, selectedChoices]);

  const handleChangeResponse = (index: number, multiple: boolean) => () => {
    let newChoices: number[] = [];
    if (multiple) {
      if (selectedChoices.includes(index)) {
        window.flightLogger.log("[NonLongFormPollResponseForm] Removing choice index=", index);
        newChoices = selectedChoices.filter((choice) => choice !== index);
      } else {
        window.flightLogger.log("[NonLongFormPollResponseForm] Adding choice index=", index);
        newChoices = selectedChoices.concat(index);
      }
    } else {
      newChoices = [index];
    }
    window.flightLogger.log(`[NonLongFormPollResponseForm] Setting selected choices=${newChoices}`);
    setSelectedChoices(newChoices);
  };

  const handleEdit = () => {
    if (!pollResponse?.id) {
      console.error("[NonLongFormPollResponseForm] Trying to edit a poll response without an id");
      return;
    }
    window.flightLogger.log("[NonLongFormPollResponseForm] Editing response");
    dispatch(
      createOrUpdatePollResponse({
        id: pollResponse.id,
        isDraft: true,
        submittedAt: null,
      }),
    );
  };

  if (!readOnly && pollSession.status === PollSessionStatusType.Completed) return null;

  return (
    <form data-testid="non-long-form-poll-response-form">
      {isPollCompleted && !poll.student_visible && (
        <div className="mb5">
          <PollVisibility isStudentVisible={poll.student_visible} />
        </div>
      )}
      <Box mb={7}>
        {choices.map((choice: PollChoiceType, index: number) =>
          poll.multiple_response ? (
            <Checkbox
              key={choice.id}
              value={selectedChoices.includes(choice.index)}
              onChange={handleChangeResponse(choice.index, true)}
              disabled={!isDraft || isPollCompleted}
              retainColorWhenDisabled
              color={CHOICE_COLORS[index % CHOICE_COLORS.length]}
              textSize="body"
              data-testid="checkbox-poll-choice"
              my={2}
            >
              {numberToLetter(choice.index)}. {choice.choice}
            </Checkbox>
          ) : (
            <RadioButton
              key={choice.id}
              checked={selectedChoices.includes(choice.index)}
              onChange={handleChangeResponse(choice.index, false)}
              option={choice.index}
              disabled={!isDraft || isPollCompleted}
              retainColorWhenDisabled
              color={CHOICE_COLORS[index % CHOICE_COLORS.length]}
              textSize="body"
              data-testid="radio-button-poll-choice"
              my={2}
            >
              {numberToLetter(choice.index)}. {choice.choice}
            </RadioButton>
          ),
        )}
      </Box>
      {isPollCompleted ? null : isDraft ? (
        isMobile ? (
          <Button
            kind="primary-mobile"
            className="w-full"
            data-testid="submit-poll-button"
            onClick={handleSubmit}
            disabled={selectedChoices.length === 0}
          >
            Submit
          </Button>
        ) : (
          <>
            <PollVisibility isStudentVisible={poll.student_visible} />
            <Button
              className="mt2 mb4"
              onClick={handleSubmit}
              disabled={selectedChoices.length === 0}
              data-testid="submit-poll-button"
            >
              Submit
            </Button>
          </>
        )
      ) : null}
      {!isDraft && !isPollCompleted && (
        <PollConfirmationTextAndEditButton isMobile={isMobile} handleEdit={handleEdit} />
      )}
    </form>
  );
}

export { NonLongFormPollResponseForm };
